import { useState, type FormEvent, type JSX } from 'react';
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  LockIcon,
  StatusIndicator,
  Textarea
} from '@viniciomendesr/rayrovisk-design-system';
import { useDeleteInterest, useInterest, useSaveInterest } from '../api/queries';

/**
 * Screen 11. A product that does not exist yet, so there is nothing to open —
 * only a way to say she wants it. The registered state comes from a refetch,
 * not from local state: what she sees after submitting is what the server
 * actually stored.
 */
export function AnalysisInterestScreen(): JSX.Element {
  const interest = useInterest();
  const save = useSaveInterest();
  const remove = useDeleteInterest();
  const [handle, setHandle] = useState('');
  const [question, setQuestion] = useState('');

  const registered = interest.data?.interest ?? null;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 720 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: 'var(--rvk-text-tertiary)' }}>
            <LockIcon size={18} />
          </span>
          <Badge tone="neutral" variant="outline" size="sm">
            Em desenvolvimento
          </Badge>
        </div>
        <h1 style={{ margin: 0, font: '400 32px/1.12 var(--rvk-font-expressive)' }}>
          Análise de Instagram
        </h1>
        <p
          style={{
            margin: 0,
            font: '400 14.5px/1.6 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)'
          }}
        >
          Este produto ainda está sendo construído, então não há nada para abrir aqui por enquanto.
          A ideia é ler as suas publicações e dizer o que funciona no seu perfil, em vez de te dar
          média de mercado.
        </p>
      </div>

      <Card layer="functional" padding="lg">
        {registered ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <StatusIndicator status="live" label="Interesse registrado" detail={registered.instagramHandle} />
            <p
              style={{
                margin: 0,
                font: '400 14px/1.6 var(--rvk-font-functional)',
                color: 'var(--rvk-text-secondary)'
              }}
            >
              Você entra na primeira leva de testes e recebe um e-mail quando abrir. Sem cobrança
              nenhuma até lá.
            </p>
            <div>
              <Button
                variant="ghost"
                size="sm"
                loading={remove.isPending}
                onClick={() => void remove.mutateAsync()}
              >
                Cancelar meu interesse
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              void save.mutateAsync({
                instagramHandle: handle,
                question: question.trim() === '' ? null : question
              });
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ font: '400 19px/1.2 var(--rvk-font-expressive)' }}>
                Quer avisar que tem interesse?
              </span>
              <p
                style={{
                  margin: 0,
                  font: '400 13.5px/1.55 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                Deixe o seu Instagram e o que você mais gostaria de entender do seu perfil. Isso
                ajuda a decidir o que entra primeiro.
              </p>
            </div>
            <Field label="Seu @ no Instagram">
              <Input
                required
                placeholder="@seuperfil"
                value={handle}
                onChange={(event) => setHandle(event.target.value)}
              />
            </Field>
            <Field label="O que você gostaria de descobrir" hint="Opcional, em uma ou duas frases.">
              <Textarea
                rows={3}
                placeholder="ex.: quais vídeos trazem encomenda de verdade"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
              />
            </Field>
            {save.isError && (
              <p style={{ margin: 0, font: '400 13px/1.5 var(--rvk-font-functional)' }}>
                {(save.error as Error).message}
              </p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Button type="submit" variant="primary" loading={save.isPending}>
                Quero ser avisada
              </Button>
              <span
                style={{
                  font: '400 12.5px/1.4 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-tertiary)'
                }}
              >
                Nada é cobrado agora.
              </span>
            </div>
          </form>
        )}
      </Card>
    </section>
  );
}
